import React from 'react';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import StoredFileLink from '~/components/Documents/StoredFileLink';
import type { DocumentWithCliveInfo } from '~/components/Documents/utils/cliveDocumentHelpers';
import { useCms } from '~/components/hooks/useCms';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import { getLocalDateToday } from '~/DateTimeUtils';
import { reportAxiosError } from '~/Utils';

import DocumentFieldsSection from './DocumentFieldsSection/DocumentFieldsSection';
import { documentFieldsValidation } from './utils';

interface RetryFailedUploadDialogProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  document: DocumentWithCliveInfo;
  claim: ClaimPolymorph;
}

const RetryFailedUploadDialog: React.FC<RetryFailedUploadDialogProps> = ({
  open,
  onCancel,
  onSuccess,
  document,
  claim,
}) => {
  const { user } = useCms();

  if (!open) {
    return null;
  }

  // Get file information from the uploading document
  const storedFileId = document?.stored_file_id;
  const originalFilename = document?.document_name;
  const fileSize = document?.size || 0;

  // Get the user's related exposure as default (matches main upload flow logic)
  const relatedExposure = claim?.exposures?.find((exposure) => exposure?.handling_adjuster_id === user.id);

  const initialValues = {
    claim_id: claim.id,
    type: '',
    document_name: '',
    document_date: getLocalDateToday(),
    document_date_received: getLocalDateToday(),
    exposure_ids: relatedExposure ? [relatedExposure.id] : [],
    summary: '',
    last_modified: getLocalDateToday(),
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object().shape(documentFieldsValidation())}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        try {
          const payload = {
            exposure_ids: values.exposure_ids,
            last_modified: values.last_modified,
            type: values.type,
            summary: values.summary,
            file_size: fileSize,
            document_name: values.document_name || originalFilename,
            document_date: values.document_date,
            document_date_received: values.document_date_received,
            stored_file_id: storedFileId,
          };

          // Create a new document from the stored file. The existing CliveStoreInformation
          // record will be automatically linked to this new document.
          await axios.post(`/api/v1/claims/${claim.id}/documents/retry_failed_upload`, {
            documents: [payload],
          });

          await onSuccess();
          onCancel();
        } catch (error) {
          await reportAxiosError(error);
          setSubmitting(false);
        }
      }}
      enableReinitialize
    >
      {({ isSubmitting, handleSubmit }) => {
        return (
          <CardDialog
            isDialog
            open={open}
            title="Upload Document"
            maxWidth="xs"
            onClose={onCancel}
            preventClose={isSubmitting}
          >
            <div className="mb-16">
              <strong>File:</strong> <StoredFileLink storedFileId={storedFileId} filename={originalFilename || ''} />
            </div>

            <DocumentFieldsSection claim={claim} disabled={isSubmitting} fieldsTitle="Document" />
            <div className="mt-16 flex justify-end gap-8">
              <CancelButton onClick={onCancel} disabled={isSubmitting} />
              <Button variant="contained" color="primary" onClick={() => handleSubmit()} disabled={isSubmitting}>
                Upload
              </Button>
            </div>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

export default RetryFailedUploadDialog;
